import React, { useMemo, useState } from 'react';

import { Select } from '@vlabs/uikit/components/controls/select/Select';
import type { SelectProps } from '@vlabs/uikit/components/controls/select/Select';
import type { TOption } from '@vlabs/uikit/types';
import type { ActionMeta } from 'react-select';

import { selectMenuPortalProps } from '../select-portal';

function getFlatOptions(options: SelectProps['options'] = []): TOption[] {
  return options.flatMap((option) => {
    if ('options' in option) {
      return option.options;
    }

    return [option];
  });
}

export function ValueSelect({
  isMulti = false,
  onChange,
  options = [],
  value,
  name,
  ...selectProps
}: SelectProps): React.JSX.Element {
  const [selectedOption, setSelectedOption] = useState<TOption | TOption[] | null>(null);

  const onChangeHandler = (newValue: TOption | TOption[], actionMeta: ActionMeta<TOption>) => {
    setSelectedOption(newValue);

    if (!newValue) {
      onChange?.(newValue, actionMeta);
      return;
    }

    let $value;
    if (isMulti && Array.isArray(newValue)) $value = newValue.map(({ value: optionValue }: TOption) => optionValue);
    if (!isMulti) $value = newValue.value;

    onChange?.($value, actionMeta);
  };

  const selectValue = useMemo(() => {
    const flatOptions = getFlatOptions(options);

    if (isMulti) {
      if (!Array.isArray(value)) return [];

      // если Select синхронный, то ищем выбранные значения в обычных options
      const optionsByValue = flatOptions.filter((option) => value.includes(option.value));
      if (optionsByValue.length > 0) return optionsByValue;

      // если isAsync, то выбранной опции нет в options, потому что они приходят через loadOptions
      // поэтому возвращаем сохраненую опцию, чтобы отобразить label
      if (Array.isArray(selectedOption)) {
        return selectedOption.filter((option) => value.includes(option.value));
      }

      return [];
    }

    // если Select синхронный, то ищем выбранные значения в обычных options
    const optionFromList = flatOptions.find((option) => option.value === value);
    if (optionFromList) return optionFromList;

    // если isAsync, то выбранной опции нет в options, потому что они приходят через loadOptions
    // поэтому возвращаем сохраненую опцию, чтобы отобразить label
    if (selectedOption?.value === value) return selectedOption;

    return null;
  }, [isMulti, options, selectedOption, value]);

  return (
    <Select
      isMulti={isMulti}
      name={name}
      onChange={onChangeHandler}
      options={options}
      value={selectValue}
      {...selectMenuPortalProps}
      {...selectProps}
    />
  );
}
